import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtLogCompact } from "@/lib/tz";
import { SEGMENTS } from "@/components/SendSmsDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2, Send, AlertTriangle, Megaphone } from "lucide-react";
import { toast } from "sonner";

const CRIMSON = "#bf3b4f";

/** Text the workers who are eligible for a job-board posting, in one tap. */
export function JobBlastDialog({ shift, onOpenChange }) {
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [message, setMessage] = useState("");
  const [templates, setTemplates] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!shift) return;
    setData(null); setPicked(new Set()); setMessage("");
    api.blastAudience(shift).then((d) => {
      setData(d);
      setMessage(d.suggested_message || "");
      setPicked(new Set(d.recipients.filter((r) => r.textable).map((r) => r.id)));
    }).catch((e) => {
      toast.error(e.response?.data?.detail || "Could not work out who to text.");
      onOpenChange(false);
    });
    api.smsTemplates().then((r) => setTemplates(r.templates || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shift]);

  const toggle = (id) => {
    const next = new Set(picked);
    next.has(id) ? next.delete(id) : next.add(id);
    setPicked(next);
  };

  const send = async () => {
    setSending(true);
    try {
      const res = await api.blastSend(shift, { recipients: [...picked], message: message.trim() });
      if (res.failed) toast.error(`${res.sent} texted, ${res.failed} failed.`);
      else toast.success(`Texted ${res.sent} worker${res.sent === 1 ? "" : "s"}.`);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send the blast.");
    } finally { setSending(false); }
  };

  const recips = data?.recipients || [];
  return (
    <Dialog open={!!shift} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] flex flex-col sm:max-w-2xl" data-testid="blast-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Megaphone className="h-4 w-4" style={{ color: CRIMSON }} />Text workers about this shift
          </DialogTitle>
        </DialogHeader>

        {!data ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : (
          <div className="overflow-y-auto space-y-3 pr-1">
            {data.last_blast && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-800 flex items-start gap-1.5"
                data-testid="blast-last-warning">
                <AlertTriangle className="h-3.5 w-3.5 mt-px shrink-0" />
                Already texted {data.last_blast.count} worker{data.last_blast.count === 1 ? "" : "s"} about this shift
                {data.last_blast.by ? ` by ${data.last_blast.by}` : ""} · {fmtLogCompact(data.last_blast.at)}.
              </div>
            )}

            <div className="text-[11px] text-slate-500">
              These workers can see this posting (incompatible workers and anyone outside the
              posting's audience are already excluded). The participant's name is never included
              in the text.
            </div>

            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold text-slate-700">
                {picked.size} of {recips.filter((r) => r.textable).length} selected
              </div>
              <div className="flex gap-1.5">
                <Button variant="outline" size="sm" className="h-7 text-[11px]" data-testid="blast-select-all"
                  onClick={() => setPicked(new Set(recips.filter((r) => r.textable).map((r) => r.id)))}>Select all</Button>
                <Button variant="outline" size="sm" className="h-7 text-[11px]" data-testid="blast-select-none"
                  onClick={() => setPicked(new Set())}>Clear</Button>
              </div>
            </div>

            <div className="max-h-52 overflow-y-auto rounded-md border border-slate-200 divide-y divide-slate-100" data-testid="blast-recipients">
              {recips.length === 0 && <div className="px-2.5 py-3 text-[11px] text-slate-400">No eligible workers for this posting.</div>}
              {recips.map((r) => (
                <label key={r.id} className={`flex items-center gap-2 px-2.5 py-1.5 ${r.textable ? "cursor-pointer hover:bg-slate-50" : "opacity-60"}`}
                  data-testid={`blast-recipient-${r.id}`}>
                  <input type="checkbox" disabled={!r.textable} checked={picked.has(r.id)} onChange={() => toggle(r.id)}
                    className="h-3.5 w-3.5 accent-[#bf3b4f]" data-testid={`blast-check-${r.id}`} />
                  <span className="text-xs text-slate-800 flex-1 min-w-0 truncate">{r.name}</span>
                  {r.is_preferred && <Badge className="text-[9px] py-0 bg-rose-50 text-[#bf3b4f] border border-rose-200">Preferred</Badge>}
                  {r.applied && <Badge className="text-[9px] py-0 bg-slate-100 text-slate-600 border border-slate-200">Applied</Badge>}
                  {r.clashes > 0 && <Badge className="text-[9px] py-0 bg-red-50 text-red-700 border border-red-200">Rostered that day</Badge>}
                  {!r.textable && <span className="text-[10px] text-slate-400">no mobile</span>}
                </label>
              ))}
            </div>

            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold text-slate-700">Message</div>
              {templates.length > 0 && (
                <Select onValueChange={(v) => setMessage(templates.find((t) => t.id === v)?.body || "")}>
                  <SelectTrigger className="h-7 w-44 text-[11px]" data-testid="blast-template"><SelectValue placeholder="Use a template" /></SelectTrigger>
                  <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id} className="text-xs">{t.title}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} className="text-sm"
              data-testid="blast-message" />
            <div className="text-[10px] text-slate-400">
              {message.length} characters · {SEGMENTS(message)} SMS segment{SEGMENTS(message) === 1 ? "" : "s"} each ·
              {" "}sent from “Prydes”, so workers can't reply
            </div>
          </div>
        )}

        <DialogFooter className="shrink-0 border-t border-slate-100 pt-3">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" style={{ backgroundColor: CRIMSON }} onClick={send} data-testid="blast-send"
            disabled={sending || !picked.size || !message.trim()}>
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
            Text {picked.size} worker{picked.size === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
